import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import SASDashboard from '../../components/dashboard/SASDashboard';
import VerbaendeDashboard from '../../components/dashboard/VerbaendeDashboard';
import NewsletterDashboard from '../../components/dashboard/NewsletterDashboard';
import './AdminPlatformDashboard.css';

const API = (path) => `/api${path}`;
const authHeaders = () => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` });

const TABS = [
  { key: 'overview',    kanji: '概',  label: 'Übersicht' },
  { key: 'lizenzen',    kanji: '商',  label: 'Kunden & Lizenzen' },
  { key: 'verbaende',   kanji: '連',  label: 'Verbände' },
  { key: 'newsletter',  kanji: '報',  label: 'Newsletter' },
];

export default function AdminPlatformDashboard() {
  const navigate = useNavigate();
  const [tab, setTab]   = useState('overview');
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState(null);
  const [recentTenants, setRecentTenants] = useState([]);

  // Auth guard
  useEffect(() => {
    const token = localStorage.getItem('token');
    const raw = localStorage.getItem('adminData') || localStorage.getItem('clubData');
    if (!token || !raw) { navigate('/dashboard', { replace: true }); return; }
    try {
      const parsed = JSON.parse(raw);
      const u = parsed.user;
      const isSuperAdmin = u?.verband_id_tenant === null || u?.verband_id_tenant === undefined;
      if (!isSuperAdmin) { navigate('/dashboard', { replace: true }); return; }
      setUser(u);
    } catch { navigate('/dashboard', { replace: true }); }
  }, [navigate]);

  const loadOverview = useCallback(async () => {
    try {
      const [sRes, tRes] = await Promise.all([
        fetch(API('/sas/stats'), { headers: authHeaders() }),
        fetch(API('/sas/tenants'), { headers: authHeaders() }),
      ]);
      const [sData, tData] = await Promise.all([sRes.json(), tRes.json()]);
      if (sData.success) setStats(sData.data);
      if (tData.success) setRecentTenants(tData.data.slice(0, 6));
    } catch (e) { console.error('Admin overview load error:', e); }
  }, []);

  useEffect(() => {
    if (user && tab === 'overview') loadOverview();
  }, [user, tab, loadOverview]);

  if (!user) return null;

  return (
    <div className="adm-platform">

      {/* ── HEADER ─────────────────────────────────────────────── */}
      <header className="adm-header">
        <button className="adm-back" onClick={() => navigate('/dashboard')}>
          ‹ Dashboard
        </button>
        <div className="adm-header-brand">
          <span className="adm-header-kanji">管理</span>
          <div className="adm-header-text">
            <span className="adm-header-title">Admin Plattform</span>
            <span className="adm-header-sub">Super-Admin · Systemverwaltung</span>
          </div>
        </div>
        <div className="adm-header-user">
          <span className="adm-user-name">{user.name || user.email}</span>
          <span className="adm-user-badge">Super Admin</span>
        </div>
      </header>

      {/* ── TAB NAV ────────────────────────────────────────────── */}
      <nav className="adm-tabs">
        {TABS.map(t => (
          <button
            key={t.key}
            className={`adm-tab${tab === t.key ? ' adm-tab--active' : ''}`}
            onClick={() => setTab(t.key)}
          >
            <span className="adm-tab-kanji">{t.kanji}</span>
            <span className="adm-tab-label">{t.label}</span>
          </button>
        ))}
      </nav>

      {/* ── CONTENT ────────────────────────────────────────────── */}
      <main className="adm-content">

        {/* ÜBERSICHT */}
        {tab === 'overview' && (
          <div className="adm-overview">
            {stats ? (
              <>
                <div className="adm-kpi-grid">
                  <KpiCard kanji="総" label="Tenants gesamt"   value={stats.tenants.gesamt}  color="#d4af37" />
                  <KpiCard kanji="有" label="Aktiv"            value={stats.tenants.aktiv}   color="#10b981" />
                  <KpiCard kanji="試" label="Testphase"        value={stats.tenants.trial}   color="#f59e0b" />
                  <KpiCard kanji="停" label="Gesperrt"         value={stats.tenants.gesperrt} color="#ef4444" />
                  <KpiCard kanji="未" label="Offene Beträge"   value={fmt(stats.umsatz?.gesamt_offen)}  color="#94a3b8" isText />
                  <KpiCard kanji="済" label="Bezahlt gesamt"   value={fmt(stats.umsatz?.gesamt_bezahlt)} color="#10b981" isText />
                </div>

                <div className="adm-section-label">Zuletzt registriert</div>
                <div className="adm-tenant-grid">
                  {recentTenants.map(t => (
                    <TenantMiniCard key={t.id} tenant={t} onOpen={() => setTab('lizenzen')} />
                  ))}
                  {recentTenants.length === 0 && (
                    <div className="adm-empty">Noch keine Tenants</div>
                  )}
                </div>

                <div className="adm-quick-actions">
                  <button className="adm-quick-btn" onClick={() => setTab('lizenzen')}>
                    <span className="adm-quick-kanji">顧</span>
                    <span>Kunden verwalten</span>
                  </button>
                  <button className="adm-quick-btn" onClick={() => setTab('verbaende')}>
                    <span className="adm-quick-kanji">連</span>
                    <span>Verbände verwalten</span>
                  </button>
                </div>
              </>
            ) : (
              <div className="adm-loading">
                <div className="adm-loading-spinner" />
                <span>Lade Plattform-Daten…</span>
              </div>
            )}
          </div>
        )}

        {/* KUNDEN & LIZENZEN (SASDashboard) */}
        {tab === 'lizenzen' && (
          <div className="adm-sas-wrapper">
            <SASDashboard />
          </div>
        )}

        {/* VERBÄNDE */}
        {tab === 'verbaende' && (
          <div className="adm-verbaende-wrapper">
            <VerbaendeDashboard />
          </div>
        )}

        {/* NEWSLETTER */}
        {tab === 'newsletter' && (
          <div className="adm-newsletter-wrapper">
            <NewsletterDashboard />
          </div>
        )}

      </main>
    </div>
  );
}

// ── KPI-Karte ─────────────────────────────────────────────────
function KpiCard({ kanji, label, value, color, isText }) {
  return (
    <div className="adm-kpi-card">
      <div className="adm-kpi-kanji" style={{ color }}>{kanji}</div>
      <div className="adm-kpi-value" style={{ color: isText ? color : '#fff' }}>{value}</div>
      <div className="adm-kpi-label">{label}</div>
    </div>
  );
}

// ── Tenant Mini-Card ──────────────────────────────────────────
const STATUS_COLOR = { trial: '#f59e0b', active: '#10b981', suspended: '#ef4444', cancelled: '#6b7280' };
const STATUS_LABEL = { trial: 'Test', active: 'Aktiv', suspended: 'Gesperrt', cancelled: 'Gekündigt' };

function TenantMiniCard({ tenant: t, onOpen }) {
  return (
    <div className="adm-tenant-card" onClick={onOpen}>
      <div className="adm-tenant-name">{t.name}</div>
      <div className="adm-tenant-meta">
        <code className="adm-tenant-domain">{t.subdomain}</code>
        <span className="adm-tenant-status" style={{ background: STATUS_COLOR[t.status] || '#555' }}>
          {STATUS_LABEL[t.status] || t.status}
        </span>
      </div>
    </div>
  );
}

function fmt(n) {
  return Number(n || 0).toFixed(2).replace('.', ',') + ' €';
}
